import React from 'react';
import { TraderChatDashboard } from './TraderChatDashboard';
import { TradingStrategy } from '../trading/TradingStrategy';
import StewieQuantStrategyProvider from '../trading/StewieQuantStrategyProvider';
import { TestingHub } from '../trading/TestingHub';
import { RiskAlertsDashboard } from '../trading/RiskAlerts';
import { TradingActivity } from '../trading/TradingActivity';
import { RolesInterface } from '../roles/RolesInterface';
import { Strategy } from '../trading/types';
import { 
  RiskAlertsEmptyState, 
  TradingActivityEmptyState
} from './EmptyStateRenderers';
// Core dashboard components only

interface TraderDashboardContentProps {
  messages: any[];
  inputMessage: string;
  setInputMessage: (message: string) => void;
  isLoading: boolean;
  handleSendMessage: (e: React.FormEvent) => void;
  isExpanded: boolean;
  setIsExpanded: (expanded: boolean) => void;
  showRoles: boolean;
  setShowRoles: (show: boolean) => void;
  activeRole: any;
  setActiveRole: (role: any) => void;
  roles: any[];
  selectedStrategy: string;
  setSelectedStrategy: (strategy: string) => void;
  activeStrategies: Strategy[];
  displayPositions: any;
  handleNewStrategy: (data: any) => void;
  handleDeleteStrategy?: (strategyId: string) => void; // Add optional deletion handler
}

export const TraderDashboardContent = ({
  messages,
  inputMessage,
  setInputMessage,
  isLoading,
  handleSendMessage,
  isExpanded,
  setIsExpanded,
  showRoles,
  setShowRoles,
  activeRole,
  setActiveRole,
  roles,
  selectedStrategy,
  setSelectedStrategy,
  activeStrategies,
  displayPositions,
  handleNewStrategy,
  handleDeleteStrategy // Add to destructuring
}: TraderDashboardContentProps) => {
  
  // Determine which components to render based on strategy existence
  const RiskAlertsComponent = activeStrategies.length === 0 
    ? RiskAlertsEmptyState 
    : RiskAlertsDashboard;
  
  const TradingActivityComponent = activeStrategies.length === 0 
    ? TradingActivityEmptyState 
    : TradingActivity;
    
  // Get the first strategy ID to use for TradingActivity if available
  const defaultStrategyId = activeStrategies.length > 0 ? activeStrategies[0].id || '' : '';
    
  return (
    <div className="space-y-6">
      {showRoles && (
        <RolesInterface 
          roles={roles}
          activeRole={activeRole}
          setActiveRole={setActiveRole}
          showRoles={showRoles}
          setShowRoles={setShowRoles}
          setInputMessage={setInputMessage}
        />
      )}
      <div className="w-full">
        <TraderChatDashboard 
          messages={messages}
          inputMessage={inputMessage}
          setInputMessage={setInputMessage}
          isLoading={isLoading}
          handleSendMessage={handleSendMessage}
          isExpanded={isExpanded}
          setIsExpanded={setIsExpanded}
          activeRole={activeRole}
        />
      </div>
  
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <TradingStrategy
          selectedStrategy={selectedStrategy}
          setSelectedStrategy={setSelectedStrategy}
          strategies={activeStrategies}
          onNewStrategy={handleNewStrategy}
          onDeleteStrategy={handleDeleteStrategy}
        />
        <StewieQuantStrategyProvider
          onAddStrategy={handleNewStrategy}
          activeStrategy={activeStrategies.find(s => s.id === selectedStrategy) || null}
        />
        <div className="transition-all duration-300 ease-in-out col-span-3 lg:col-span-1">
          <TestingHub onNewStrategy={handleNewStrategy} />
        </div>
      </div>
      
      {/* Core dashboard elements only - No duplicate StewieShield or PortfolioOverview here */}
  
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RiskAlertsComponent />
        <TradingActivityComponent strategyId={selectedStrategy || defaultStrategyId} />
      </div>
    </div>
  );
};
